/* ============================================================================
   PRESET BUNDLES — the kit size drawer and Product Details, for set.html
   (Figma 1647:80121 · Size Drawer/Unselected D 1594:89633 · M 1594:89642).

   One drawer shops the whole bundle. Each product in it gets a Kit Product
   Card (its ghost in the page's colour, the name, VIEW DETAILS), its sizes
   with SIZE CHART, and its second variant — Size Type for the top, Length for
   the pant — and the top carries ADD EMBROIDERY (the shared configurator,
   src/figs-embroidery/, wired in main.js). One ADD at the foot adds
   every product at once, pinned so it's always in reach on a long sheet.

   What's being decided:
     • Several size picks in one drawer without losing the shopper. Nothing is
       preselected (the PDP's review call). Each pick confirms in its label
       ("Size: X-Small"); a product left half-done points at what it still
       needs, and finishing one brings the next one up if it's off screen.
     • ADD before every pick is made doesn't fail silently: Quick Buy's error
       copy appears under the first thing missing, which scrolls into view and
       shakes, and focus lands on it.
     • Large is sold out (data.js isKitSoldOut): its tile, and every type /
       length while it's marked, take the PDP's Soldout strike and stay
       choosable, as on the PDP. With no waitlist for a set, ADD stops on it
       like a missing pick: "Large is sold out…" under that product.
     • Closing without ADD keeps the picks (2026-10-12): a complete, in-stock
       set lands on the page as chosen — ghosts, sizes, CHANGE SIZE, the CTA
       on ADD — without adding it. Partial picks wait in the drawer.
     • A successful add confirms on the button (the spring, licensed for
       add-to-bag), bumps the bag by the number of products, and the drawer
       closes itself — then the page takes the picks (label + ADD), so one
       tap adds another set.
   SIZE CHART and VIEW DETAILS swap this panel out and hand back to it when
   dismissed, focused on the link that opened them, with every pick kept.
   ========================================================================== */

import { animate, stagger, MO, state, play, track, SPRING_RELEASE, $, $$ } from './mo.js';
import { createDrawer } from './drawers.js';
import { SIZE_NAMES, moneyShort } from './data.js';
import {
  renderSizes, renderTypes, checkTile, markSoldOut, bindRoving, swapText, swapButtonLabel, pulse, shake, announce, tm,
} from './ui.js';

/* the second variant's heading: tops "Size Type", pants "Length" */
const typeHeading = (c) => (!c.typeLabel || c.typeLabel === 'Type' ? 'Size Type' : c.typeLabel);
const MISSING = {
  size: () => 'Please select a size to add this to your bag.',
  type: (c) => `Please select a ${typeHeading(c).toLowerCase()} to add this to your bag.`,
  soldout: (c, size) => `${SIZE_NAMES[size] || size} is sold out. Please select another size.`,
};

const itemHTML = (c) => `
  <section class="kit-item" data-kit-item="${c.id}" aria-labelledby="kit-${c.id}-name">
    <div class="kit-card">
      <span class="kit-card__media"><img class="kit-card__img" alt="" decoding="async" data-kit-ghost></span>
      <div class="kit-card__content">
        <h3 class="kit-card__name" id="kit-${c.id}-name">${tm(c.name)}</h3>
        <button class="pdp-link kit-card__details" type="button" aria-haspopup="dialog" data-kit-details data-hover-link>
          <span class="trim">View Details</span><span class="u-sr">: ${c.plainName}</span>
        </button>
      </div>
    </div>
    <div class="size-drawer__group" data-kit-group="size">
      <div class="size-drawer__label-row">
        <p class="size-drawer__label trim" id="kit-${c.id}-size" data-kit-label="size">Select Size</p>
        <button class="pdp-link" type="button" aria-haspopup="dialog" data-kit-chart data-hover-link>
          <span class="trim">Size Chart</span><span class="u-sr">: ${c.plainName}</span>
        </button>
      </div>
      <div class="size-grid" role="radiogroup" aria-labelledby="kit-${c.id}-name kit-${c.id}-size" data-kit-grid="size"></div>
      <p class="qb__error kit__error" data-kit-error="size" hidden>
        <img src="assets/figma/icon-warning.svg" alt="" width="16" height="16"><span>${MISSING.size(c)}</span>
      </p>
    </div>
    <div class="size-drawer__group" data-kit-group="type">
      <p class="size-drawer__label trim" id="kit-${c.id}-type" data-kit-label="type">Select ${typeHeading(c)}</p>
      <div class="type-grid" role="radiogroup" aria-labelledby="kit-${c.id}-name kit-${c.id}-type" data-kit-grid="type"></div>
      <p class="qb__error kit__error" data-kit-error="type" hidden>
        <img src="assets/figma/icon-warning.svg" alt="" width="16" height="16"><span>${MISSING.type(c)}</span>
      </p>
    </div>
    ${c.embroidery ? `
    <a class="kit-emb" href="#embroidery" role="button" aria-haspopup="dialog" data-kit-emb data-press>
      <span class="kit-emb__label">Add Embroidery</span><span class="u-sr"> to the ${c.plainName}</span>
    </a>
    <div class="kit-emb-added" data-kit-emb-added hidden></div>` : ''}
  </section>`;

/* the ATB: bag + ADD on mobile, ADD TO BAG on desktop (the two frames) */
const ATB_HTML = '<img class="kit__atb-icon" src="assets/site/icon-bag.svg" alt="" width="20" height="20"><span>Add</span><span class="kit__atb-more"> to Bag</span>';

/* getColor() → the page colour's slug; isSoldOut(colorSlug, size) */
export function initKitDrawer({ product, getColor, onAdd, onCommit, onSizeChart, onDetails, isSoldOut = () => false }) {
  const root = $('[data-drawer="kit"]');
  const drawer = createDrawer(root);
  const scroller = $('[data-kit]', root);
  const items = $('[data-kit-items]', root);
  const atb = $('[data-kit-atb]', root);
  const atbLabel = $('[data-kit-atb-label]', root);
  $('[data-kit-title]', root).innerHTML = `${tm(product.name)}<span class="size-drawer__title-suffix">—${moneyShort(product.price)}</span>`;
  items.innerHTML = product.components.map(itemHTML).join('');
  atbLabel.innerHTML = ATB_HTML;

  const parts = product.components.map((c) => {
    const el = $(`[data-kit-item="${c.id}"]`, items);
    return {
      c, el,
      ghost: $('[data-kit-ghost]', el),
      grid: { size: $('[data-kit-grid="size"]', el), type: $('[data-kit-grid="type"]', el) },
      label: { size: $('[data-kit-label="size"]', el), type: $('[data-kit-label="type"]', el) },
      error: { size: $('[data-kit-error="size"]', el), type: $('[data-kit-error="type"]', el) },
    };
  });
  /* `draft` is what's marked; `committed` is what the page has (set by ADD,
     or by closing on a complete, in-stock set of picks) */
  const blank = () => Object.fromEntries(product.components.map((c) => [c.id, { size: null, type: c.types.length === 1 ? c.types[0] : null }]));
  let draft = blank();
  let committed = null;
  let busy = false;
  let pending = false; // added, page not yet updated
  let closeTimer = 0;
  let pageTrigger = null;

  const labelHTML = {
    size: (c, v) => (v ? `Size: ${SIZE_NAMES[v] || v}` : 'Select Size'),
    type: (c, v) => (v ? `${typeHeading(c)}: ${v}` : `Select ${typeHeading(c)}`),
  };
  const done = (p) => Boolean(draft[p.c.id].size && draft[p.c.id].type);
  const complete = () => parts.every(done);
  const out = (size) => Boolean(size) && isSoldOut(getColor(), size);
  /* the first thing stopping ADD, in reading order: a missing pick, or a
     size that's sold out */
  const firstMissing = () => {
    for (const p of parts) {
      if (!draft[p.c.id].size) return [p, 'size'];
      if (out(draft[p.c.id].size)) return [p, 'size', 'soldout'];
      if (!draft[p.c.id].type) return [p, 'type'];
    }
    return null;
  };
  /* sold out in the page's colour: the size, and every type / length of it */
  const syncSoldOut = (p) => {
    markSoldOut(p.grid.size, (t) => out(t.dataset.size));
    markSoldOut(p.grid.type, () => out(draft[p.c.id].size));
  };

  function clearError(p, key) {
    const err = p.error[key];
    p.grid[key].removeAttribute('data-invalid');
    if (err.hidden) return;
    play(err, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => { err.hidden = true; });
  }

  /* a finished product brings the next unfinished one into view — only when
     it's off screen, so the sheet never jumps under a tap */
  function cueNext(from) {
    const next = parts.slice(parts.indexOf(from) + 1).find((p) => !done(p));
    if (!next) return;
    const key = draft[next.c.id].size ? 'type' : 'size';
    const group = next.grid[key].closest('.size-drawer__group');
    const r = group.getBoundingClientRect();
    const view = scroller.getBoundingClientRect();
    const actions = $('.kit__actions', root).offsetHeight;
    if (r.top > view.bottom - actions - 40) {
      setTimeout(() => {
        next.el.scrollIntoView({ block: 'start', behavior: state.reduced ? 'auto' : 'smooth' });
      }, state.reduced ? 0 : 260);
    }
    setTimeout(() => pulse(key === 'size' ? next.label.size.parentElement : next.label.type), state.reduced ? 0 : 420);
  }

  function pick(p, key, value, tile) {
    draft[p.c.id][key] = value;
    checkTile(p.grid[key], key, value);
    play(tile, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
    swapText(p.label[key], labelHTML[key](p.c, value));
    clearError(p, key);
    syncSoldOut(p);
    const other = key === 'size' ? 'type' : 'size';
    const needs = !draft[p.c.id][other];
    const name = key === 'size' ? (SIZE_NAMES[value] || value) : `${value} ${typeHeading(p.c).toLowerCase()}`;
    if (key === 'size' && out(value)) {
      announce(`${p.c.shortName}: ${name} selected, sold out. Select another size to add the set.`);
      return;
    }
    if (needs) {
      announce(`${p.c.shortName}: ${name} selected. Select ${other === 'size' ? 'a size' : `a ${typeHeading(p.c).toLowerCase()}`}.`);
      pulse(other === 'size' ? p.label.size.parentElement : p.label.type); // points at what's still missing
      return;
    }
    announce(`${p.c.shortName}: ${name} selected.${complete() ? ` ${product.name} is ready to add.` : ''}`);
    if (complete()) pulse(atb);
    else cueNext(p);
  }

  parts.forEach((p) => {
    renderSizes(p.grid.size, p.c.sizes, null);
    renderTypes(p.grid.type, p.c.types, draft[p.c.id].type);
    syncSoldOut(p);
    p.grid.type.closest('.size-drawer__group').hidden = p.c.types.length < 2;
    /* arrows move focus without choosing (Enter / Space choose), like the PDP */
    bindRoving(p.grid.size, (tile) => pick(p, 'size', tile.dataset.size, tile), { selectOnArrow: false });
    bindRoving(p.grid.type, (tile) => pick(p, 'type', tile.dataset.type, tile), { selectOnArrow: false });
  });

  /* SIZE CHART / VIEW DETAILS swap this panel out; dismissing them comes back
     here, on the link that opened them, with every pick as it was */
  let handingOff = false; // the drawer stepping aside for them, not being closed on the picks
  const handOff = (fn, p) => (e) => {
    const link = e.currentTarget;
    handingOff = true;
    drawer.close(); // drawer:close fires inside this call
    handingOff = false;
    fn?.(link, () => api.open(pageTrigger, { focus: link }), { component: p.c, ...draft[p.c.id] });
  };
  parts.forEach((p) => {
    $('[data-kit-chart]', p.el).addEventListener('click', handOff(onSizeChart, p));
    $('[data-kit-details]', p.el).addEventListener('click', handOff(onDetails, p));
  });

  atb.addEventListener('click', async () => {
    if (busy) return;
    const missing = firstMissing();
    if (missing) {
      const [p, key, why = key] = missing;
      const err = p.error[key];
      const msg = MISSING[why](p.c, draft[p.c.id].size);
      $('span', err).textContent = msg;
      err.hidden = false;
      p.grid[key].setAttribute('data-invalid', '');
      announce(`${p.c.shortName}: ${msg}`);
      p.grid[key].closest('.size-drawer__group').scrollIntoView({ block: 'center', behavior: state.reduced ? 'auto' : 'smooth' });
      setTimeout(() => {
        play(err, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
        shake(p.grid[key]);
        p.grid[key].querySelector('[role="radio"]')?.focus({ preventScroll: true });
      }, state.reduced ? 0 : 280);
      return;
    }
    busy = true;
    pending = true;
    committed = structuredClone(draft);
    onAdd(structuredClone(committed));
    pulse(atb);
    await swapButtonLabel(atbLabel, 'Added to Bag', { affirm: true });
    if (pending) closeTimer = setTimeout(() => drawer.close(), state.reduced ? 600 : 1100);
  });

  /* however the drawer goes after an add — on its own, or dismissed during
     the confirmation — the page takes the picks as it leaves, so the change
     is seen; the button resets once it's out of sight. Closed without ADD,
     a complete, in-stock set of picks still lands on the page — chosen, not
     added (firstMissing() is null only then; a sold-out pick stays here). */
  root.addEventListener('drawer:close', () => {
    clearTimeout(closeTimer);
    if (pending) {
      pending = false;
      setTimeout(() => onCommit(structuredClone(committed), { added: true }), state.reduced ? 0 : 140);
      setTimeout(() => { busy = false; atbLabel.innerHTML = ATB_HTML; }, state.reduced ? 150 : 450);
      return;
    }
    if (handingOff || firstMissing()) return;
    if (committed && JSON.stringify(committed) === JSON.stringify(draft)) return;
    committed = structuredClone(draft);
    setTimeout(() => onCommit(structuredClone(committed), { added: false }), state.reduced ? 0 : 140);
  });

  /* every card's ghost follows the page's colour */
  function syncGhosts() {
    const slug = getColor();
    parts.forEach((p) => {
      const src = p.c.ghost(slug);
      if (p.ghost.getAttribute('src') !== src) p.ghost.src = src;
    });
  }
  syncGhosts();

  const api = {
    /* `focus` is the hand-back from the size chart / product details: land on
       that link, keep the page trigger and what's marked, skip the assembly */
    open(trigger, { focus } = {}) {
      const handBack = Boolean(focus);
      if (!handBack) {
        pageTrigger = trigger || document.activeElement;
        if (committed) draft = structuredClone(committed); // a fresh open marks what the page has
      }
      syncGhosts();
      parts.forEach((p) => {
        syncSoldOut(p);
        ['size', 'type'].forEach((key) => {
          const v = draft[p.c.id][key];
          checkTile(p.grid[key], key, v);
          if (!v) p.grid[key].querySelector('[role="radio"]').tabIndex = 0;
          p.label[key].innerHTML = labelHTML[key](p.c, v);
          p.error[key].hidden = true;
          p.grid[key].removeAttribute('data-invalid');
        });
      });
      root.querySelectorAll('[data-autofocus]').forEach((el) => el.removeAttribute('data-autofocus'));
      const first = parts[0].grid.size;
      (focus || first.querySelector('[aria-checked="true"]') || first.querySelector('[role="radio"]')).setAttribute('data-autofocus', '');
      drawer.open(pageTrigger);
      if (!handBack) scroller.scrollTop = 0;
      if (!state.reduced && !handBack) {
        /* each product's card and grid assemble behind the arriving panel,
           product by product, finishing inside the panel's own travel */
        play(parts.flatMap((p) => [p.el.firstElementChild, ...p.grid.size.children]), { opacity: [0, 1], y: [MO.riseXs, 0] },
          { duration: MO.base, ease: MO.eEntrance, delay: stagger(0.012, { startDelay: 0.16 }) });
      }
    },
    /* picks set from outside (a signed-in shopper's saved sizes): the page
       has them, and the drawer opens on them */
    preset(picks) {
      draft = structuredClone(picks);
      committed = structuredClone(picks);
    },
    get value() { return committed && structuredClone(committed); },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
  return api;
}

/* ============================================================================
   PRODUCT DETAILS — the kit page's View Details modal (wearfigs.com), on the
   drawer shell: the product in the page's colour, VIEW PRODUCT (its PDP
   prototype in the lab), then Details & Fit, Fabric & Care and Shipping &
   Returns as one-open-at-a-time accordions, and Shop More. Opened from the
   kit drawer it hands back to it; from the triptych it returns to the page.
   getColor() → the page colour (the swatch object).
   ========================================================================== */
export function initProductDetails({ product, getColor }) {
  const root = $('[data-drawer="details"]');
  const drawer = createDrawer(root);
  const scroller = $('[data-pd]', root);
  const img = $('[data-pd-img]', root);
  const name = $('[data-pd-name]', root);
  const view = $('[data-pd-view]', root);
  const acc = $('[data-pd-acc]', root);
  const more = $('[data-pd-more]', root);

  const list = (lines) => `<ul class="pd__list">${lines.map((l) => `<li>${tm(l)}</li>`).join('')}</ul>`;
  const section = (id, title, body, open) => `
    <section class="pd__section">
      <h3 class="pd__heading">
        <button class="pd__toggle" type="button" aria-expanded="${open}" aria-controls="pd-${id}" id="pd-${id}-btn" data-pd-toggle>
          <span>${title}</span>
          <svg class="pd__icon" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M1 5h8"/><path class="pd__icon-v" d="M5 1v8"/></svg>
        </button>
      </h3>
      <div class="pd__panel" id="pd-${id}" role="region" aria-labelledby="pd-${id}-btn" ${open ? '' : 'hidden'}>${body}</div>
    </section>`;

  acc.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pd-toggle]');
    if (!btn) return;
    const opening = btn.getAttribute('aria-expanded') !== 'true';
    $$('[data-pd-toggle]', acc).forEach((b) => {
      const on = b === btn && opening;
      b.setAttribute('aria-expanded', String(on));
      const panel = $(`#${b.getAttribute('aria-controls')}`, acc);
      panel.hidden = !on;
      if (on) play(panel, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
    });
  });

  return {
    open(c, trigger, { onDismiss } = {}) {
      const color = getColor();
      img.src = c.image(color.slug);
      img.alt = `${c.plainName} in ${color.name}`;
      name.innerHTML = tm(c.name);
      /* the top and the wide-leg have PDP prototypes here (in the page's
         colour); the rest open live */
      const external = /^https?:/.test(c.page);
      view.href = external ? c.page : `${c.page}?color=${color.slug}`;
      view.target = external ? '_blank' : '';
      view.rel = external ? 'noopener' : '';
      acc.innerHTML = [
        section('fit', 'Details &amp; Fit', `${list(c.fit)}${list(c.features)}`, true),
        section('fabric', 'Fabric &amp; Care', list(c.fabric), false),
        section('shipping', 'Shipping &amp; Returns', product.shipping.map((p) => `<p class="pd__copy">${p}</p>`).join(''), false),
      ].join('');
      /* SHOP MORE: the collections the Scrub Pants PLP stands in for, and the
         colour — Charcoal has its own PLP */
      const colorPlp = color.slug === 'charcoal' ? '/experiments/13-plp-charcoal/' : '/experiments/03-plp-prototype/';
      more.innerHTML = [...c.shopMore.map((m) => [m, '/experiments/03-plp-prototype/']), [color.name, colorPlp]]
        .map(([m, href]) => `<a class="pd__chip" href="${href}">${m}</a>`).join('');
      scroller.scrollTop = 0;
      drawer.open(trigger, { onDismiss });
      if (!state.reduced) {
        play($$('.pd__product, .pd__section, .pd__more:not([hidden])', root), { opacity: [0, 1], y: [MO.riseSm, 0] },
          { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.16 }) });
      }
    },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
}
